import {
  ClaimReservationWithTimeslot,
  GetClaimReservationsRequest,
  ClaimCenterWithBookables,
  GetClaimCentersRequest,
  ClaimCenterBookableAndSchedule,
  GetClaimCenterBookablesForDateRequest,
  ScheduleExceptionTemplate,
  ClaimCenterScheduleException,
  CreateClaimCenterExceptionRequest,
  ClaimCenterBookableScheduleException,
  CreateClaimCenterBookableExceptionRequest,
  ClaimCenterScheduleExceptions,
  GetClaimCenterExceptionsForDateRequest,
  GetClaimCenterExceptionsForRangeRequest,
  DeleteClaimReservationRequest,
  ClaimReservation,
  EditClaimReservationRequest,
  EditClaimReservationOperationRequest,
  ClaimCenterBookableSchedules,
  ScheduleClaimCenterBookableOperationRequest,
  DeleteScheduleExceptionRequest,
  ClaimCenterAvailability,
  GetClaimCenterAvailabilityRequest,
  AdminClaimReservationRequest,
  ClaimReservationResponse,
  CreateClaimCenterReservationRequest,
  ClaimReservationsApi,
  UnschedulePreviewResponse,
  GetUnschedulePreviewRequest,
  UnscheduleBookableResponse,
  UnscheduleClaimCenterBookableRequest,
  SetClaimReservationCheckedInRequest,
  ClearClaimReservationCheckedInRequest,
  SetClaimReservationNoShowRequest,
  ClearClaimReservationNoShowRequest,
  ResendClaimFormNotificationResponse,
  ResendClaimFormNotificationOperationRequest,
  ClaimReservationCommunicationMethod,
  PageClaimReservationResponse,
  SearchClaimReservationsRequest,
} from '@Generated/back-office-client/src'
import { BackOfficeDataServiceAPI } from './back-office-data-service'

const getReservations = async (
  claimCenterId: string,
  startDate: string,
  endDate: string,
): Promise<ClaimReservationWithTimeslot[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: GetClaimReservationsRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    startDate: new Date(startDate),
    endDate: new Date(endDate),
  }

  return (await api.getClaimReservations(request)).reservations
}

const getClaimCenters = async (): Promise<ClaimCenterWithBookables[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: GetClaimCentersRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
  }

  return (await api.getClaimCenters(request)).claimCenters
}

const getClaimCenterBookablesForDate = async (
  claimCenterId: string,
  date: string,
): Promise<ClaimCenterBookableAndSchedule[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: GetClaimCenterBookablesForDateRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    date,
  }

  return (await api.getClaimCenterBookablesForDate(request)).bookables
}

const createClaimCenterException = async (
  claimCenterId: string,
  exception: ScheduleExceptionTemplate,
): Promise<ClaimCenterScheduleException> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: CreateClaimCenterExceptionRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    scheduleExceptionTemplate: exception,
  }

  return await api.createClaimCenterException(request)
}

const createClaimCenterBookableException = async (
  claimCenterId: string,
  bookableId: string,
  exception: ScheduleExceptionTemplate,
): Promise<ClaimCenterBookableScheduleException> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: CreateClaimCenterBookableExceptionRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    bookableId,
    scheduleExceptionTemplate: exception,
  }

  return await api.createClaimCenterBookableException(request)
}

const getClaimCenterExceptionsForDate = async (
  claimCenterId: string,
  localDate: string,
): Promise<ClaimCenterScheduleExceptions> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: GetClaimCenterExceptionsForDateRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    date: localDate,
  }

  return await api.getClaimCenterExceptionsForDate(request)
}

const getClaimCenterExceptionsForDateRange = async (
  startDate: string,
  endDate: string,
): Promise<Array<ClaimCenterScheduleException>> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: GetClaimCenterExceptionsForRangeRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    localStartDate: startDate,
    localEndDate: endDate,
  }

  return (await api.getClaimCenterExceptionsForRange(request)).claimCenterExceptions
}

const cancelReservation = async (claimReservationId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: DeleteClaimReservationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
  }

  return await api.deleteClaimReservation(request)
}

const editReservation = async (
  claimReservationId: string,
  editRequest: EditClaimReservationRequest,
): Promise<ClaimReservation> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: EditClaimReservationOperationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
    editClaimReservationRequest: editRequest,
  }

  return await api.editClaimReservation(request)
}

const scheduleBookableForDate = async (
  claimCenterId: string,
  localDate: string,
): Promise<ClaimCenterBookableSchedules> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: ScheduleClaimCenterBookableOperationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    scheduleClaimCenterBookableRequest: {
      localDate,
    },
  }

  return await api.scheduleClaimCenterBookable(request)
}

const cancelException = async (claimCenterId: string, exceptionId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: DeleteScheduleExceptionRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    exceptionId,
  }

  return await api.deleteScheduleException(request)
}

const getClaimCentersAvailability = async (
  date: string,
  claimCenterId: string,
  numberOfTickets: number,
  excludeReservationId?: string,
): Promise<ClaimCenterAvailability[]> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)

  let durationInMinutes
  if (numberOfTickets < 6) {
    durationInMinutes = 15
  } else if (numberOfTickets < 10) {
    durationInMinutes = 30
  } else if (numberOfTickets < 15) {
    durationInMinutes = 45
  } else {
    durationInMinutes = 60
  }

  const request: GetClaimCenterAvailabilityRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    date,
    claimCenterId,
    durationInMinutes,
    excludeReservationId,
  }

  return (await api.getClaimCenterAvailability(request)).availability
}

const createReservation = async (
  claimCenterId: string,
  adminClaimReservationRequest: AdminClaimReservationRequest,
): Promise<ClaimReservationResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)

  const request: CreateClaimCenterReservationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    adminClaimReservationRequest,
  }

  return await api.createClaimCenterReservation(request)
}

const getUnschedulePreview = async (
  claimCenterId: string,
  bookableId: string,
  localDate: string,
): Promise<UnschedulePreviewResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: GetUnschedulePreviewRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    bookableId,
    localDate,
  }

  return await api.getUnschedulePreview(request)
}

const unscheduleBookable = async (
  claimCenterId: string,
  bookableId: string,
  localDate: string,
): Promise<UnscheduleBookableResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: UnscheduleClaimCenterBookableRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimCenterId,
    bookableId,
    unscheduleBookableRequest: {
      localDate,
    },
  }

  return await api.unscheduleClaimCenterBookable(request)
}

const setCheckedIn = async (claimReservationId: string): Promise<ClaimReservation> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: SetClaimReservationCheckedInRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
  }
  return await api.setClaimReservationCheckedIn(request)
}

const clearCheckedIn = async (claimReservationId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: ClearClaimReservationCheckedInRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
  }
  return await api.clearClaimReservationCheckedIn(request)
}

const setNoShow = async (claimReservationId: string): Promise<ClaimReservation> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: SetClaimReservationNoShowRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
  }
  return await api.setClaimReservationNoShow(request)
}

const clearNoShow = async (claimReservationId: string): Promise<void> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: ClearClaimReservationNoShowRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
  }
  return await api.clearClaimReservationNoShow(request)
}

const searchReservations = async (
  query: string,
  resultStart: number,
  resultEnd: number,
  startDate?: string,
  includeCancelled?: boolean,
): Promise<PageClaimReservationResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: SearchClaimReservationsRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    query,
    resultStart,
    resultEnd,
    startDate,
    includeCancelled,
  }

  return await api.searchClaimReservations(request)
}

const resendClaimFormNotification = async (
  claimReservationId: string,
  method: ClaimReservationCommunicationMethod,
): Promise<ResendClaimFormNotificationResponse> => {
  const api = BackOfficeDataServiceAPI.getBackOfficeApi(ClaimReservationsApi)
  const request: ResendClaimFormNotificationOperationRequest = {
    customerId: BackOfficeDataServiceAPI.getBackOfficeConfig().customerId,
    claimReservationId,
    resendClaimFormNotificationRequest: {
      method,
    },
  }

  return await api.resendClaimFormNotification(request)
}

export const ClaimReservationsServiceAPI = {
  getReservations,
  getClaimCenters,
  getClaimCenterBookablesForDate,
  createClaimCenterException,
  createClaimCenterBookableException,
  getClaimCenterExceptionsForDate,
  getClaimCenterExceptionsForDateRange,
  cancelReservation,
  editReservation,
  scheduleBookableForDate,
  cancelException,
  getClaimCentersAvailability,
  createReservation,
  getUnschedulePreview,
  unscheduleBookable,
  setCheckedIn,
  clearCheckedIn,
  setNoShow,
  clearNoShow,
  resendClaimFormNotification,
  searchReservations,
}
